import { useState, useRef, useEffect, useCallback } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import { Download, Check, X, FileText, Pen } from "lucide-react"

interface PublicQuoteItem {
  name: string
  description: string | null
  quantity: number
  unit: string
  unit_price_net: number
  discount_percent: number
  vat_rate: number
  line_total_net: number
  line_total_gross: number
}

interface PublicQuote {
  number: string
  status: string
  currency: string
  total_net: string
  total_vat: string
  total_gross: string
  issue_date: string | null
  valid_until: string | null
  version: number
  notes: string | null
  terms: string | null
  recipient_name: string | null
  recipient_email: string | null
  company_name: string | null
  company_nip: string | null
  owner_name: string | null
  accepted_at: string | null
  rejected_at: string | null
  signed_at: string | null
  has_signature: boolean
  items: PublicQuoteItem[]
}

function formatDate(d: string | null): string {
  if (!d) return "—"
  const date = new Date(d)
  return date.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" })
}

function formatMoney(val: string | number, currency = "PLN"): string {
  const n = typeof val === "string" ? parseFloat(val) : val
  return `${n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
}

const statusLabels: Record<string, string> = {
  draft: "Szkic",
  sent: "Wysłana",
  viewed: "Wyświetlona",
  accepted: "Zaakceptowana",
  rejected: "Odrzucona",
  expired: "Wygasła",
  cancelled: "Anulowana",
}

const statusColors: Record<string, string> = {
  draft: "#94a3b8",
  sent: "#3b82f6",
  viewed: "#8b5cf6",
  accepted: "#22c55e",
  rejected: "#ef4444",
  expired: "#f59e0b",
  cancelled: "#6b7280",
}

// --- Signature Canvas Component ---
function SignatureCanvas({
  onSignatureChange,
}: {
  onSignatureChange: (data: string | null) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasDrawn, setHasDrawn] = useState(false)

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    if ("touches" in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      }
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    const ctx = canvasRef.current?.getContext("2d")
    if (!ctx) return
    const pos = getPos(e)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
    setIsDrawing(true)
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return
    e.preventDefault()
    const ctx = canvasRef.current?.getContext("2d")
    if (!ctx) return
    const pos = getPos(e)
    ctx.lineTo(pos.x, pos.y)
    ctx.strokeStyle = "#1e293b"
    ctx.lineWidth = 2
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.stroke()
    setHasDrawn(true)
  }

  const endDraw = () => {
    setIsDrawing(false)
    if (hasDrawn && canvasRef.current) {
      onSignatureChange(canvasRef.current.toDataURL("image/png"))
    }
  }

  const clear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasDrawn(false)
    onSignatureChange(null)
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = canvas.offsetWidth
    canvas.height = canvas.offsetHeight
  }, [])

  return (
    <div>
      <div
        style={{
          border: "1px dashed #cbd5e1",
          borderRadius: 8,
          background: "#fff",
          position: "relative",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: 150, display: "block", cursor: "crosshair" }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!hasDrawn && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#94a3b8",
              fontSize: 14,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Pen size={16} />
            Narysuj podpis tutaj
          </div>
        )}
      </div>
      {hasDrawn && (
        <button
          onClick={clear}
          style={{
            marginTop: 6,
            fontSize: 13,
            color: "#64748b",
            background: "none",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Wyczyść podpis
        </button>
      )}
    </div>
  )
}

// --- Main Page ---
export function PublicQuotePage() {
  const { token } = useParams<{ token: string }>()
  const [showAcceptDialog, setShowAcceptDialog] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [signerName, setSignerName] = useState("")
  const [signatureData, setSignatureData] = useState<string | null>(null)
  const [actionDone, setActionDone] = useState<"accepted" | "rejected" | null>(null)

  const {
    data: quote,
    isLoading,
    error,
    refetch,
  } = useQuery<PublicQuote>({
    queryKey: ["public-quote", token],
    queryFn: async () => {
      const res = await api.get<{ data: PublicQuote }>(`/api/v1/public/quotes/${token}`)
      return res.data
    },
    enabled: !!token,
  })

  const acceptMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/v1/public/quotes/${token}/accept`, {
        signature_name: signerName || undefined,
        signature_data: signatureData || undefined,
      })
    },
    onSuccess: () => {
      setActionDone("accepted")
      setShowAcceptDialog(false)
      refetch()
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/api/v1/public/quotes/${token}/reject`)
    },
    onSuccess: () => {
      setActionDone("rejected")
      setShowRejectDialog(false)
      refetch()
    },
  })

  const handleDownloadPdf = useCallback(() => {
    window.open(`/api/v1/public/quotes/${token}/pdf`, "_blank")
  }, [token])

  if (isLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loading}>
            <FileText size={48} color="#94a3b8" />
            <p style={{ color: "#64748b", marginTop: 16 }}>Ładowanie oferty...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !quote) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.loading}>
            <FileText size={48} color="#ef4444" />
            <p style={{ color: "#ef4444", marginTop: 16, fontWeight: 600 }}>
              Oferta nie została znaleziona
            </p>
            <p style={{ color: "#64748b", marginTop: 8, fontSize: 14 }}>
              Link do oferty może być nieprawidłowy lub oferta została usunięta.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const isExpired = quote.valid_until && new Date(quote.valid_until) < new Date()
  const canAct = ["sent", "viewed"].includes(quote.status) && !isExpired

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#3D5AFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              O
            </div>
            <span style={{ fontWeight: 600, color: "#1e293b" }}>OkeyCRM</span>
          </div>
          <button onClick={handleDownloadPdf} style={styles.pdfBtn}>
            <Download size={16} />
            Pobierz PDF
          </button>
        </div>

        {/* Quote Card */}
        <div style={styles.card}>
          {/* Title + Status */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                Oferta {quote.number}
              </h1>
              {quote.version > 1 && (
                <span style={{ fontSize: 13, color: "#64748b" }}>Wersja {quote.version}</span>
              )}
            </div>
            <span
              style={{
                ...styles.badge,
                background: `${statusColors[quote.status]}18`,
                color: statusColors[quote.status],
                borderColor: `${statusColors[quote.status]}40`,
              }}
            >
              {statusLabels[quote.status] || quote.status}
            </span>
          </div>

          {/* Meta Info */}
          <div style={styles.metaGrid}>
            {quote.owner_name && (
              <div>
                <div style={styles.metaLabel}>Wystawił</div>
                <div style={styles.metaValue}>{quote.owner_name}</div>
              </div>
            )}
            {quote.company_name && (
              <div>
                <div style={styles.metaLabel}>Dla</div>
                <div style={styles.metaValue}>{quote.company_name}</div>
                {quote.company_nip && (
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>NIP: {quote.company_nip}</div>
                )}
              </div>
            )}
            {quote.recipient_name && (
              <div>
                <div style={styles.metaLabel}>Odbiorca</div>
                <div style={styles.metaValue}>{quote.recipient_name}</div>
                {quote.recipient_email && (
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>{quote.recipient_email}</div>
                )}
              </div>
            )}
            <div>
              <div style={styles.metaLabel}>Data wystawienia</div>
              <div style={styles.metaValue}>{formatDate(quote.issue_date)}</div>
            </div>
            <div>
              <div style={styles.metaLabel}>Ważna do</div>
              <div
                style={{
                  ...styles.metaValue,
                  color: isExpired ? "#ef4444" : "#1e293b",
                  fontWeight: isExpired ? 600 : 400,
                }}
              >
                {formatDate(quote.valid_until)}
                {isExpired && " (wygasła)"}
              </div>
            </div>
          </div>

          {/* Separator */}
          <hr style={styles.separator} />

          {/* Items Table */}
          <h2 style={styles.sectionTitle}>Pozycje</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 40, textAlign: "center" }}>Lp.</th>
                  <th style={styles.th}>Pozycja</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Ilość</th>
                  <th style={{ ...styles.th, textAlign: "center" }}>J.m.</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Cena netto</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Rabat</th>
                  <th style={{ ...styles.th, textAlign: "center" }}>VAT</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Netto</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Brutto</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.map((item, i) => (
                  <tr key={i}>
                    <td style={{ ...styles.td, textAlign: "center" }}>{i + 1}</td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 500 }}>{item.name}</div>
                      {item.description && (
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>{item.quantity}</td>
                    <td style={{ ...styles.td, textAlign: "center" }}>{item.unit}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      {formatMoney(item.unit_price_net, "")}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      {item.discount_percent > 0 ? `${item.discount_percent}%` : "—"}
                    </td>
                    <td style={{ ...styles.td, textAlign: "center" }}>{item.vat_rate}%</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      {formatMoney(item.line_total_net, "")}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      {formatMoney(item.line_total_gross, "")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={styles.totalsBox}>
            <div style={styles.totalsRow}>
              <span>Suma netto:</span>
              <span>{formatMoney(quote.total_net, quote.currency)}</span>
            </div>
            <div style={styles.totalsRow}>
              <span>VAT:</span>
              <span>{formatMoney(quote.total_vat, quote.currency)}</span>
            </div>
            <div
              style={{
                ...styles.totalsRow,
                fontWeight: 700,
                fontSize: 18,
                color: "#3D5AFE",
                borderTop: "2px solid #3D5AFE",
                paddingTop: 8,
                marginTop: 4,
              }}
            >
              <span>Suma brutto:</span>
              <span>{formatMoney(quote.total_gross, quote.currency)}</span>
            </div>
          </div>

          {/* Notes / Terms */}
          {quote.notes && (
            <div style={{ marginTop: 24 }}>
              <h3 style={styles.sectionTitle}>Uwagi</h3>
              <p style={{ color: "#475569", whiteSpace: "pre-wrap", fontSize: 14 }}>
                {quote.notes}
              </p>
            </div>
          )}
          {quote.terms && (
            <div style={{ marginTop: 16 }}>
              <h3 style={styles.sectionTitle}>Warunki</h3>
              <p style={{ color: "#475569", whiteSpace: "pre-wrap", fontSize: 14 }}>
                {quote.terms}
              </p>
            </div>
          )}

          {/* Accepted/Rejected Banner */}
          {quote.status === "accepted" && (
            <div
              style={{
                ...styles.statusBanner,
                background: "#f0fdf4",
                borderColor: "#bbf7d0",
                color: "#15803d",
              }}
            >
              <Check size={20} />
              <div>
                <div style={{ fontWeight: 600 }}>Oferta zaakceptowana</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  {quote.accepted_at && formatDate(quote.accepted_at)}
                  {quote.has_signature && " — podpisano"}
                </div>
              </div>
            </div>
          )}
          {quote.status === "rejected" && (
            <div
              style={{
                ...styles.statusBanner,
                background: "#fef2f2",
                borderColor: "#fecaca",
                color: "#dc2626",
              }}
            >
              <X size={20} />
              <div>
                <div style={{ fontWeight: 600 }}>Oferta odrzucona</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  {quote.rejected_at && formatDate(quote.rejected_at)}
                </div>
              </div>
            </div>
          )}
          {isExpired && quote.status !== "accepted" && quote.status !== "rejected" && (
            <div
              style={{
                ...styles.statusBanner,
                background: "#fffbeb",
                borderColor: "#fde68a",
                color: "#b45309",
              }}
            >
              <X size={20} />
              <div>
                <div style={{ fontWeight: 600 }}>Oferta wygasła</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  Termin ważności minął {formatDate(quote.valid_until)}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {canAct && !actionDone && (
            <div style={styles.actionBar}>
              <button
                onClick={() => setShowAcceptDialog(true)}
                style={styles.acceptBtn}
              >
                <Check size={18} />
                Zaakceptuj ofertę
              </button>
              <button
                onClick={() => setShowRejectDialog(true)}
                style={styles.rejectBtn}
              >
                <X size={18} />
                Odrzuć
              </button>
            </div>
          )}

          {actionDone === "accepted" && (
            <div
              style={{
                ...styles.statusBanner,
                background: "#f0fdf4",
                borderColor: "#bbf7d0",
                color: "#15803d",
              }}
            >
              <Check size={20} />
              <div style={{ fontWeight: 600 }}>
                Dziękujemy! Oferta została zaakceptowana.
              </div>
            </div>
          )}
          {actionDone === "rejected" && (
            <div
              style={{
                ...styles.statusBanner,
                background: "#fef2f2",
                borderColor: "#fecaca",
                color: "#dc2626",
              }}
            >
              <X size={20} />
              <div style={{ fontWeight: 600 }}>Oferta została odrzucona.</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <span>Wygenerowano w OkeyCRM</span>
        </div>
      </div>

      {/* Accept Dialog Overlay */}
      {showAcceptDialog && (
        <div style={styles.overlay} onClick={() => setShowAcceptDialog(false)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px 0", color: "#1e293b" }}>
              Akceptacja oferty
            </h2>
            <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 20px 0" }}>
              Potwierdź akceptację oferty {quote.number}. Możesz opcjonalnie złożyć podpis.
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={styles.inputLabel}>Imię i nazwisko</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="np. Jan Kowalski"
                style={styles.input}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={styles.inputLabel}>Podpis (opcjonalnie)</label>
              <SignatureCanvas onSignatureChange={setSignatureData} />
            </div>

            {acceptMutation.error && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#dc2626",
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                {(acceptMutation.error as Error).message}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowAcceptDialog(false)}
                style={styles.cancelBtn}
              >
                Anuluj
              </button>
              <button
                onClick={() => acceptMutation.mutate()}
                disabled={acceptMutation.isPending}
                style={{
                  ...styles.acceptBtn,
                  opacity: acceptMutation.isPending ? 0.6 : 1,
                }}
              >
                <Check size={16} />
                {acceptMutation.isPending ? "Akceptowanie..." : "Zaakceptuj"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog Overlay */}
      {showRejectDialog && (
        <div style={styles.overlay} onClick={() => setShowRejectDialog(false)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px 0", color: "#1e293b" }}>
              Odrzucenie oferty
            </h2>
            <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 20px 0" }}>
              Czy na pewno chcesz odrzucić ofertę {quote.number}?
            </p>

            {rejectMutation.error && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#dc2626",
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                {(rejectMutation.error as Error).message}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowRejectDialog(false)}
                style={styles.cancelBtn}
              >
                Anuluj
              </button>
              <button
                onClick={() => rejectMutation.mutate()}
                disabled={rejectMutation.isPending}
                style={{
                  ...styles.rejectBtn,
                  opacity: rejectMutation.isPending ? 0.6 : 1,
                }}
              >
                <X size={16} />
                {rejectMutation.isPending ? "Odrzucanie..." : "Odrzuć ofertę"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Styles ---
const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#f1f5f9",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    padding: "24px 16px",
  },
  container: {
    maxWidth: 900,
    margin: "0 auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  pdfBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    background: "#fff",
    color: "#475569",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    padding: 32,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  badge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    border: "1px solid",
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 16,
    marginTop: 20,
  },
  metaLabel: {
    fontSize: 12,
    color: "#94a3b8",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 14,
    color: "#1e293b",
  },
  separator: {
    border: "none",
    borderTop: "1px solid #e2e8f0",
    margin: "24px 0",
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#1e293b",
    margin: "0 0 12px 0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: 14,
  },
  th: {
    textAlign: "left" as const,
    padding: "10px 8px",
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    borderBottom: "2px solid #e2e8f0",
    background: "#f8fafc",
  },
  td: {
    padding: "10px 8px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155",
  },
  totalsBox: {
    maxWidth: 300,
    marginLeft: "auto",
    marginTop: 16,
  },
  totalsRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: 14,
    color: "#475569",
  },
  actionBar: {
    display: "flex",
    gap: 12,
    marginTop: 32,
    paddingTop: 24,
    borderTop: "1px solid #e2e8f0",
    justifyContent: "center",
    flexWrap: "wrap" as const,
  },
  acceptBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 24px",
    border: "none",
    borderRadius: 8,
    background: "#22c55e",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  rejectBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 24px",
    border: "1px solid #fecaca",
    borderRadius: 8,
    background: "#fff",
    color: "#ef4444",
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
  },
  cancelBtn: {
    padding: "8px 16px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    background: "#fff",
    color: "#64748b",
    fontSize: 14,
    cursor: "pointer",
  },
  statusBanner: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 20px",
    borderRadius: 8,
    border: "1px solid",
    marginTop: 24,
  },
  footer: {
    textAlign: "center" as const,
    marginTop: 20,
    color: "#94a3b8",
    fontSize: 13,
  },
  loading: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 20px",
  },
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  dialog: {
    background: "#fff",
    borderRadius: 12,
    padding: 24,
    maxWidth: 480,
    width: "100%",
    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
  },
  inputLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    color: "#475569",
    marginBottom: 4,
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    fontSize: 14,
    color: "#1e293b",
    outline: "none",
    boxSizing: "border-box" as const,
  },
}
